import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

const REVIEWS = [
  { name: 'Jessica M.', avatar: 'J', color: '#f472b6', text: 'My daughter cried happy tears the first time she heard her name in a story. We use this every single night.' },
  { name: 'Sarah K.',   avatar: 'S', color: '#818cf8', text: 'Finally something that makes bedtime easy. My 4-year-old asks for it before I even suggest it.' },
  { name: 'Priya R.',   avatar: 'P', color: '#34d399', text: 'Three kids, three completely different stories every night. This app is genuinely magic.' },
];

const STORY_NAMES = ['Olivia', 'Noah', 'Sophia', 'Liam', 'Emma', 'Caleb', 'Mia', 'Ethan'];

const AdLandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [nameIdx, setNameIdx] = useState(0);
  const [nameFade, setNameFade] = useState(true);
  const [count, setCount] = useState(0);

  // Cycle child names in hero to make it feel personal
  useEffect(() => {
    const interval = setInterval(() => {
      setNameFade(false);
      setTimeout(() => {
        setNameIdx(i => (i + 1) % STORY_NAMES.length);
        setNameFade(true);
      }, 300);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Animated counter for stories generated
  useEffect(() => {
    const target = 12847;
    const duration = 1800;
    const step = target / (duration / 16);
    let current = 0;
    const timer = setInterval(() => {
      current += step;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, 16);
    return () => clearInterval(timer);
  }, []);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      navigate(`/register?email=${encodeURIComponent(email.trim())}`);
    } else {
      navigate('/register');
    }
  };

  const currentName = STORY_NAMES[nameIdx];

  return (
    <div style={{ overflowX: 'hidden', fontFamily: "'Nunito', sans-serif" }}>
      <SEO
        title="Personalised Bedtime Stories for Your Child — Try Free"
        description="AI-generated bedtime stories starring your child. Enter their name, age and interests — a unique illustrated story is ready in 15 seconds. Free to start, no credit card needed."
        noIndex={true}
      />

      {/* ── HERO ── */}
      <section style={{
        background: 'linear-gradient(160deg, #1a0533 0%, #3d1270 40%, #6f42c1 100%)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative blobs */}
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(155,89,182,0.3)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '80px', left: '-60px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(249,199,79,0.15)', filter: 'blur(50px)', pointerEvents: 'none' }} />

        {/* Top bar */}
        <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: '22px', color: '#fff' }}>
            🌙 Dreamy Bedtime
          </div>
          <Link to="/login" style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.2)', padding: '6px 16px', borderRadius: '50px' }}>
            Sign in
          </Link>
        </div>

        {/* Hero content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 20px 60px', maxWidth: '540px', margin: '0 auto', width: '100%', position: 'relative', zIndex: 1 }}>

          {/* Live counter badge */}
          <div style={{
            background: 'rgba(74,222,128,0.15)',
            border: '1px solid rgba(74,222,128,0.3)',
            borderRadius: '50px',
            padding: '6px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4ade80', display: 'inline-block', boxShadow: '0 0 6px #4ade80' }} />
            <span style={{ color: '#4ade80', fontSize: '13px', fontWeight: 700 }}>{count.toLocaleString()} stories generated today</span>
          </div>

          {/* Animated name headline */}
          <h1 style={{
            fontFamily: "'Fredoka One', cursive",
            fontSize: 'clamp(32px, 8vw, 50px)',
            color: '#fff',
            textAlign: 'center',
            lineHeight: 1.15,
            marginBottom: '16px',
          }}>
            Tonight,{' '}
            <span style={{
              color: '#f9c74f',
              display: 'inline-block',
              opacity: nameFade ? 1 : 0,
              transform: nameFade ? 'translateY(0)' : 'translateY(-8px)',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              minWidth: '120px',
            }}>
              {currentName}
            </span>{' '}
            is the hero
          </h1>

          <p style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: '17px',
            textAlign: 'center',
            lineHeight: 1.7,
            marginBottom: '10px',
            maxWidth: '440px',
          }}>
            Type your child's name → get a completely original bedtime story starring <strong style={{ color: '#fff' }}>them</strong> in <strong style={{ color: '#f9c74f' }}>15 seconds</strong>.
          </p>

          <p style={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: '14px',
            textAlign: 'center',
            marginBottom: '28px',
          }}>
            Not a template. Not a fill-in-the-blank. A real story, written just for them, every night.
          </p>

          {/* Email CTA */}
          <form onSubmit={handleEmailSubmit} style={{ width: '100%', maxWidth: '440px', marginBottom: '14px' }}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter your email — it's free"
              style={{
                width: '100%',
                padding: '16px 22px',
                borderRadius: '50px',
                border: '2px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.95)',
                fontSize: '16px',
                outline: 'none',
                marginBottom: '10px',
                boxSizing: 'border-box',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              }}
            />
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '18px',
                borderRadius: '50px',
                border: 'none',
                background: 'linear-gradient(135deg, #f9c74f 0%, #f8961e 100%)',
                color: '#1a0533',
                fontSize: '18px',
                fontWeight: 900,
                cursor: 'pointer',
                boxShadow: '0 8px 32px rgba(249,199,79,0.45)',
                letterSpacing: '0.2px',
                transition: 'transform 0.15s ease',
              }}
              onMouseEnter={e => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={e => e.target.style.transform = 'scale(1)'}
            >
              Create My Child's First Story — Free 🌙
            </button>
          </form>

          {/* Trust row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center', marginBottom: '36px' }}>
            {['✓ No credit card', '✓ 5 free stories/month', '✓ Cancel anytime'].map(t => (
              <span key={t} style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>{t}</span>
            ))}
          </div>

          {/* Floating story card preview */}
          <div style={{
            width: '100%',
            maxWidth: '420px',
            background: 'rgba(255,255,255,0.07)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '24px',
            padding: '20px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          }}>
            {/* Profile row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'linear-gradient(135deg,#f9c74f,#f8961e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>🧒</div>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                  <span style={{
                    opacity: nameFade ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                  }}>{currentName}</span>, age 6
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>Loves: Space, Dinosaurs</div>
              </div>
              <div style={{ marginLeft: 'auto', background: '#4ade80', color: '#1a4a2a', fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '50px' }}>✓ Ready</div>
            </div>

            {/* Story text */}
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '14px', padding: '14px', marginBottom: '12px' }}>
              <div style={{ fontFamily: "'Fredoka One', cursive", color: '#f9c74f', fontSize: '15px', marginBottom: '8px' }}>
                <span style={{ opacity: nameFade ? 1 : 0, transition: 'opacity 0.3s ease' }}>{currentName}</span> and the Midnight Rocket
              </div>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', lineHeight: 1.7, margin: 0 }}>
                The stars were watching as <strong style={{ color: '#fff' }}><span style={{ opacity: nameFade ? 1 : 0, transition: 'opacity 0.3s ease' }}>{currentName}</span></strong> climbed into the silver rocket. "One small step," she whispered — and the whole galaxy held its breath…
              </p>
            </div>

            {/* Tags */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {['🎨 Illustrated', '🎤 Narration', '✨ Courage theme', '⚡ 15 seconds'].map(tag => (
                <span key={tag} style={{ background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)', fontSize: '11px', padding: '4px 10px', borderRadius: '50px' }}>{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CURIOSITY HOOK ── */}
      <section style={{ background: '#fff', padding: '52px 20px' }}>
        <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '14px' }}>😭</div>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 'clamp(24px,6vw,34px)', color: '#2d1b69', marginBottom: '16px', lineHeight: 1.2 }}>
            What happens when a child hears their name in a bedtime story for the first time?
          </h2>
          <p style={{ color: '#555', fontSize: '16px', lineHeight: 1.8, marginBottom: '24px', maxWidth: '420px', margin: '0 auto 24px' }}>
            They light up. They ask you to read it again. They tell their friends the next morning. They stop dreading bedtime — and start asking for it.
          </p>
          <p style={{ color: '#888', fontSize: '14px', fontStyle: 'italic' }}>
            "She grabbed the iPad and said 'that's ME, Mummy' — I actually cried." — Jessica, mum of 2
          </p>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{ background: '#f8f5ff', padding: '56px 20px' }}>
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          <p style={{ color: '#6f42c1', fontWeight: 700, fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', textAlign: 'center', marginBottom: '8px' }}>How it works</p>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 'clamp(26px,6vw,36px)', color: '#2d1b69', textAlign: 'center', marginBottom: '36px', lineHeight: 1.2 }}>
            From zero to story in 3 taps
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              { step: '1', icon: '👶', title: "Tell us about your child", desc: "Name, age, and what they're obsessed with right now. Takes 60 seconds.", tag: '60 seconds' },
              { step: '2', icon: '🪄', title: 'Hit Generate', desc: "Pick a theme — kindness, bravery, adventure — or let us choose. Watch the magic happen.", tag: '15 seconds' },
              { step: '3', icon: '🌙', title: 'Their story is ready', desc: "Read it yourself, or tap narration and let a warm voice do it while you snuggle.", tag: 'Instant' },
            ].map(s => (
              <div key={s.step} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                background: '#fff',
                borderRadius: '18px',
                padding: '20px',
                boxShadow: '0 2px 16px rgba(111,66,193,0.08)',
              }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6f42c1, #9b59b6)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: '18px', flexShrink: 0,
                }}>{s.step}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '20px' }}>{s.icon}</span>
                    <span style={{ fontWeight: 700, color: '#2d1b69', fontSize: '15px' }}>{s.title}</span>
                    <span style={{ marginLeft: 'auto', background: '#f0e8ff', color: '#6f42c1', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '50px' }}>{s.tag}</span>
                  </div>
                  <div style={{ color: '#666', fontSize: '14px', lineHeight: 1.6 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STORY PREVIEW ── */}
      <section style={{ background: '#fff', padding: '56px 20px' }}>
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          <p style={{ color: '#6f42c1', fontWeight: 700, fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', textAlign: 'center', marginBottom: '8px' }}>See a real story</p>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 'clamp(24px,6vw,34px)', color: '#2d1b69', textAlign: 'center', marginBottom: '8px', lineHeight: 1.2 }}>
            This is what your child gets tonight
          </h2>
          <p style={{ color: '#888', fontSize: '14px', textAlign: 'center', marginBottom: '28px' }}>Generated in 15 seconds. Unique every time. Never repeated.</p>

          <div style={{
            background: 'linear-gradient(135deg, #1a0533 0%, #6f42c1 100%)',
            borderRadius: '24px',
            padding: '24px',
            boxShadow: '0 16px 48px rgba(111,66,193,0.3)',
          }}>
            {/* App header mock */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', paddingBottom: '14px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: '20px' }}>🌙</div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Dreamy Bedtime</div>
              <div style={{ marginLeft: 'auto', background: '#4ade80', color: '#1a4a2a', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '50px' }}>✓ Generated</div>
            </div>

            {/* Story */}
            <div style={{ background: '#fff', borderRadius: '16px', padding: '18px', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span style={{ fontSize: '22px' }}>🚀</span>
                <div style={{ fontFamily: "'Fredoka One', cursive", color: '#2d1b69', fontSize: '16px' }}>Emma and the Midnight Rocket</div>
              </div>
              <p style={{ color: '#444', fontSize: '13px', lineHeight: 1.8, margin: '0 0 12px' }}>
                Once upon a time, a brave girl named <strong style={{ color: '#6f42c1' }}>Emma</strong> found a tiny silver rocket in her garden. "I wonder where it goes," she whispered to the stars. The rocket blinked three times — and suddenly <strong style={{ color: '#6f42c1' }}>Emma</strong> was soaring through a galaxy of rainbow clouds…
              </p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {['📖 3 min', '✨ Courage', '🌍 English'].map(t => (
                  <span key={t} style={{ background: '#f0e8ff', color: '#6f42c1', fontSize: '11px', padding: '3px 8px', borderRadius: '50px', fontWeight: 600 }}>{t}</span>
                ))}
              </div>
            </div>

            {/* Illustration mock */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              borderRadius: '14px',
              height: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '14px',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{ textAlign: 'center', color: '#fff' }}>
                <div style={{ fontSize: '36px', marginBottom: '4px' }}>🚀✨</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>AI illustration · watercolour style</div>
              </div>
            </div>

            {/* Actions row */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.12)', borderRadius: '50px', padding: '10px', textAlign: 'center', color: '#fff', fontSize: '13px', fontWeight: 600 }}>
                🎤 Narrate
              </div>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.12)', borderRadius: '50px', padding: '10px', textAlign: 'center', color: '#fff', fontSize: '13px', fontWeight: 600 }}>
                🔁 Regenerate
              </div>
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.12)', borderRadius: '50px', padding: '10px', textAlign: 'center', color: '#fff', fontSize: '13px', fontWeight: 600 }}>
                💾 Save PDF
              </div>
            </div>
          </div>

          <p style={{ color: '#aaa', fontSize: '12px', textAlign: 'center', marginTop: '14px' }}>
            Every story is 100% original. Your child will never hear the same story twice.
          </p>
        </div>
      </section>

      {/* ── WHAT MAKES IT DIFFERENT ── */}
      <section style={{ background: '#f8f5ff', padding: '56px 20px' }}>
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 'clamp(24px,6vw,34px)', color: '#2d1b69', textAlign: 'center', marginBottom: '32px', lineHeight: 1.2 }}>
            Not just another story app 🌙
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { icon: '🧒', title: 'Your child is the main character', desc: 'Their name, age, and interests woven into every single story — not a template swap.', highlight: true },
              { icon: '🎨', title: 'Beautiful illustration every time', desc: 'Each story comes with a unique AI-generated illustration. Never the same art twice.' },
              { icon: '🎤', title: 'Narrated in your own voice', desc: 'Record 30 seconds and we clone your voice. Your child hears Mum or Dad even when you\'re away.' },
              { icon: '🧠', title: 'Stories that actually teach something', desc: 'Choose kindness, courage, friendship, gratitude — a real lesson built into every story.' },
              { icon: '👨‍👩‍👧', title: 'All your kids, all at once', desc: 'Put 3 siblings in the same story as heroes together. Or each gets their own. You choose.' },
              { icon: '📚', title: 'Story series that continue', desc: 'Start a multi-chapter adventure. Your child asks for the next chapter every night.' },
            ].map(f => (
              <div key={f.title} style={{
                display: 'flex',
                gap: '14px',
                background: f.highlight ? 'linear-gradient(135deg, #6f42c1, #9b59b6)' : '#fff',
                borderRadius: '16px',
                padding: '18px',
                boxShadow: f.highlight ? '0 8px 24px rgba(111,66,193,0.25)' : '0 2px 12px rgba(111,66,193,0.06)',
              }}>
                <div style={{ fontSize: '28px', flexShrink: 0, lineHeight: 1 }}>{f.icon}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '15px', color: f.highlight ? '#fff' : '#2d1b69', marginBottom: '4px' }}>{f.title}</div>
                  <div style={{ fontSize: '13px', lineHeight: 1.6, color: f.highlight ? 'rgba(255,255,255,0.85)' : '#666' }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section style={{ background: '#fff', padding: '56px 20px' }}>
        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 'clamp(24px,6vw,34px)', color: '#2d1b69', textAlign: 'center', marginBottom: '8px', lineHeight: 1.2 }}>
            Real parents. Real reactions. ❤️
          </h2>
          <p style={{ color: '#888', fontSize: '14px', textAlign: 'center', marginBottom: '28px' }}>Over 500 families already using Dreamy Bedtime</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {REVIEWS.map(r => (
              <div key={r.name} style={{
                background: '#f8f5ff',
                borderRadius: '18px',
                padding: '20px',
                borderLeft: `4px solid ${r.color}`,
              }}>
                <div style={{ color: '#f9c74f', fontSize: '15px', letterSpacing: '2px', marginBottom: '10px' }}>★★★★★</div>
                <p style={{ color: '#333', fontSize: '15px', lineHeight: 1.75, margin: '0 0 14px', fontStyle: 'italic' }}>"{r.text}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: r.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '14px' }}>{r.avatar}</div>
                  <div style={{ fontWeight: 700, color: '#2d1b69', fontSize: '14px' }}>{r.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{
        background: 'linear-gradient(160deg, #1a0533 0%, #3d1270 40%, #6f42c1 100%)',
        padding: '64px 20px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(249,199,79,0.08)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: '480px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>🌙</div>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 'clamp(28px,7vw,42px)', color: '#fff', marginBottom: '12px', lineHeight: 1.15 }}>
            Tonight could be different
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '16px', marginBottom: '8px', lineHeight: 1.7 }}>
            Imagine your child's face when they hear their name in a story built just for them.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '32px' }}>
            It takes 15 seconds. It's completely free to start. No card needed.
          </p>

          <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '18px' }}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Your email address"
              style={{
                width: '100%',
                padding: '16px 22px',
                borderRadius: '50px',
                border: '2px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.95)',
                fontSize: '16px',
                outline: 'none',
                boxSizing: 'border-box',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              }}
            />
            <button
              type="submit"
              style={{
                width: '100%',
                padding: '18px',
                borderRadius: '50px',
                border: 'none',
                background: 'linear-gradient(135deg, #f9c74f 0%, #f8961e 100%)',
                color: '#1a0533',
                fontSize: '18px',
                fontWeight: 900,
                cursor: 'pointer',
                boxShadow: '0 8px 32px rgba(249,199,79,0.4)',
              }}
            >
              Create My Child's First Story — Free 🌙
            </button>
          </form>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', justifyContent: 'center' }}>
            {['✓ Free forever plan', '✓ 5 stories/month', '✓ No credit card ever'].map(t => (
              <span key={t} style={{ color: 'rgba(255,255,255,0.55)', fontSize: '13px' }}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── MINIMAL FOOTER ── */}
      <footer style={{ background: '#0f0820', padding: '28px 20px', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Fredoka One', cursive", color: '#fff', fontSize: '18px', marginBottom: '8px' }}>🌙 Dreamy Bedtime</div>
        <div style={{ color: '#6b5a8a', fontSize: '12px', marginBottom: '12px' }}>© 2026 Prajapati LLC · Making bedtime magical</div>
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {[['Privacy', '/privacy'], ['Terms', '/terms'], ['Contact', '/contact']].map(([label, path]) => (
            <Link key={label} to={path} style={{ color: '#6b5a8a', fontSize: '12px', textDecoration: 'none' }}>{label}</Link>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default AdLandingPage;
